import React, { useState, useEffect } from 'react';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { Table, Alert, Select, Input, Tooltip, Modal, ConfigProvider } from 'antd';
import type { ButtonProps, SelectProps } from 'antd';
import type { ColumnsType, SortOrder, TablePaginationConfig } from 'antd/es/table/interface';
import { Link } from 'react-router-dom';
import styles from './ProdStatValidateList.module.css';
import useProdStatValidateList from './useProdStatValidateList';
import PageLayout from '@components/layout/PageLayout';
import { NSPButton } from '@components/ui/NSPButton';
import { NSPDatePicker } from '@components/ui/NSPDatepicker';
import EstimationBlade from '@components/blades/estimation-blade/EstimationBlade';
import { useEstimationBlade } from '@components/blades/estimation-blade/useEstimationBlade';
import useSession from '@hooks/useSession';
import { testPaginationItemRender, toTestAttrValue, withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';

type MeterRow = {
  meter_computed_id: number;
  location: string | null;
  meter_id: string | number;
  meter_number: string | null;
  units_delivered: number | null;
  units_consumed: number | null;
  prod_stat_netgen: number | null;
  percent_change_net_units: number | null;
  meter_read_type: string | null;
  comments?: string | null;
  remediated?: 'yes' | 'no' | 'remediated' | 'ignored';
  operationalStatus?: string | null;
  operationalStatusDate?: string | null;
  isInactiveThisMonth?: boolean | null;
  month_year?: string | null;
  approval_status?: string | null;
};

const formatPercentChange = (v: number | string | null | undefined) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toFixed(2)}%`;
};

const ProdStatValidateList = () => {
  const { isAdmin } = useSession();
  const isApprover = isAdmin;

  const {
    navigate,
    selectedPeriod,
    pickerValue,
    handleDateChange,
    data,
    loading,
    saving,
    handleEdit,
    handleSaveRow,
    handleUndoRow,
    isUndoDisabled,
    saveFeedback,
    moveFeedback,
    savedRowIds,
    availableLocations,
    isRowOriginallyApproved,
    handleDecommission,
    refetch,
  } = useProdStatValidateList();
  const [attemptedSave, setAttemptedSave] = useState<Set<number>>(new Set());
  const [selectedMeter, setSelectedMeter] = useState<MeterRow | null>(null);
  const [pendingSaveId, setPendingSaveId] = useState<number | null>(null);
  const [pendingIgnoreId, setPendingIgnoreId] = useState<number | null>(null);
  const [pendingDecommissionId, setPendingDecommissionId] = useState<number | null>(null);
  const [pendingDecommissionComputedId, setPendingDecommissionComputedId] = useState<number | null>(null);
  const [ignoredMeterIds, setIgnoredMeterIds] = useState<Set<number>>(new Set());
  const [pendingInactiveMeterIds, setPendingInactiveMeterIds] = useState<Set<number>>(new Set());
  const [showLocationWarning, setShowLocationWarning] = useState(false);

  const bladeOpen = !!selectedMeter;

  const [bladeMonthYear, setBladeMonthYear] = useState(selectedPeriod);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sync blade month when the page-level month picker changes
  useEffect(() => { setBladeMonthYear(selectedPeriod); }, [selectedPeriod]);

  // Seed ignoredMeterIds from persisted DB state when savedRowIds changes
  // (i.e. after a server save). Avoids resetting local selections on every
  // handleEdit-triggered data re-render.
  useEffect(() => {
    setIgnoredMeterIds(new Set(
      data.filter((r) => r.remediated === 'ignored').map((r) => r.meter_computed_id)
    ));
  }, [savedRowIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    meterData,
    energySource,
    regionalData,
    saving: bladeSaving,
    applyError,
    handleApply,
  } = useEstimationBlade({
    open: bladeOpen,
    meter: selectedMeter,
    monthYear: bladeMonthYear,
    onSuccess: () => {
      refetch();
      setSelectedMeter(null);
    },
  });

  const sortIcon = (label: string) =>
    ({ sortOrder }: { sortOrder?: SortOrder }) => (
      <span>
        {label}
        <span className={`${styles.sortIcon}${sortOrder ? ` ${styles.active}` : ''}`}>
          {sortOrder === 'ascend' ? '↑' : sortOrder === 'descend' ? '↓' : '⇅'}
        </span>
      </span>
    );

  // Save is enabled based on meter type:
  // - 'negative': only after user clicks Accept (remediated === 'yes')
  // - all others: always enabled
  const isSaveEnabled = (row: MeterRow): boolean => {
    if (row.meter_read_type?.toLowerCase() === 'negative') {
      return row.remediated === 'yes';
    }
    return true;
  };

  const columns: ColumnsType<MeterRow> = [
    {
      title: sortIcon('Name'),
      dataIndex: 'location',
      key: 'location',
      width: 70,
      ellipsis: true,
      sorter: (a, b) => (a.location ?? '').localeCompare(b.location ?? ''),
      sortDirections: ['ascend', 'descend', null],
      render: (_: unknown, row: MeterRow) => {
        const isDecommissioned = Boolean(row.isInactiveThisMonth) && !row.location;
        return isDecommissioned
          ? <span style={{ color: '#999' }} data-testid="inactive-label">Inactive</span>
          : (row.location ?? null);
      },
    },
    {
      title: sortIcon('Meter Id'),
      dataIndex: 'meter_number',
      key: 'meter_number',
      width: 50,
      sorter: (a, b) =>
        (a.meter_number ?? '').toString().localeCompare((b.meter_number ?? '').toString()),
      sortDirections: ['ascend', 'descend', null],
      render: (_: any, record: MeterRow) => {
        const isNewMeter = record.meter_read_type?.toLowerCase() === 'new' || record.meter_read_type?.toLowerCase() === 'new scada';
        return isNewMeter ? (
          <span style={{ color: '#999', cursor: 'not-allowed' }} data-testid="meter-number-text">{record.meter_number}</span>
        ) : (
          <Link to={`/meterreaddetails/${record.meter_id}`} data-testid="meter-number-link">{record.meter_number}</Link>
        );
      },
    },
    {
      title: sortIcon('Gen. kWh'),
      dataIndex: 'units_delivered',
      key: 'units_delivered',
      width: 70,
      align: 'left',
      render: (v: number | null) => (v == null ? '0' : Math.round(v).toLocaleString(undefined)),
      sorter: (a, b) => (a.units_delivered ?? 0) - (b.units_delivered ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: sortIcon('Used kWh'),
      dataIndex: 'units_consumed',
      key: 'units_consumed',
      width: 70,
      align: 'left',
      render: (v: number | null) => (v == null ? '0' : Math.round(v).toLocaleString(undefined)),
      sorter: (a, b) => (a.units_consumed ?? 0) - (b.units_consumed ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: sortIcon('Net Gen'),
      dataIndex: 'prod_stat_netgen',
      key: 'prod_stat_netgen',
      width: 70,
      align: 'left',
      render: (v: number | null) => (v == null ? '0' : Math.round(v).toLocaleString(undefined)),
      sorter: (a, b) => (a.prod_stat_netgen ?? 0) - (b.prod_stat_netgen ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: sortIcon('% Change (MoM)'),
      dataIndex: 'percent_change_net_units',
      key: 'percent_change_net_units',
      width: 70,
      align: 'left',
      render: formatPercentChange,
      sorter: (a, b) => (a.percent_change_net_units ?? 0) - (b.percent_change_net_units ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: sortIcon('Meter Read Type'),
      dataIndex: 'meter_read_type',
      key: 'meter_read_type',
      width: 100,
      ellipsis: true,
      sorter: (a, b) => (a.meter_read_type ?? '').localeCompare(b.meter_read_type ?? ''),
      sortDirections: ['ascend', 'descend', null],
      defaultSortOrder: 'ascend',

    },
    {
      title: 'Required Action',
      key: 'action',
      width: 120,
      ellipsis: true,
      render: (_: unknown, row: MeterRow) => {
        const isSaved = savedRowIds.has(row.meter_computed_id);
        const isApproved = row.approval_status === 'Approved';
        const type = row.meter_read_type?.toLowerCase().trim();
        const meterId = row.meter_id;

        const isInactiveForThisRow = Boolean(row.isInactiveThisMonth);
        if (isInactiveForThisRow) return null;
        if (isApproved) return null;

        // --- Negative meters: show Accept toggle (only when not yet saved) ---
        if (type === 'negative' && (row.prod_stat_netgen ?? 0) < 0 && !isSaved) {
          const isAccepted = row.remediated === 'yes';
          return (
            <NSPButton
              size="small"
              danger={isAccepted}
              disabled={isApprover}
              onClick={() =>
                handleEdit(row.meter_computed_id, 'remediated', isAccepted ? 'no' : 'yes')
              }
              data-testid="accept-button"
              data-state={isAccepted ? 'accepted' : 'not-accepted'}
            >
              {isAccepted ? 'Undo Accept' : 'Accept'}
            </NSPButton>
          );
        }

        // Once saved, clear the action cell (Save→Undo column handles it)
        if (isSaved) return null;

        if ((type === 'estimate') || (type === 'zero'))
          return (
            <button
              onClick={() => setSelectedMeter(row)}
              data-testid="estimate-link"
              style={{
                color: '#1890ff',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
              }}
            >
              Estimate
            </button>
          );
        // if (type === 'zero') return <Link to="/estimation-dashboard">Estimate</Link>;
        if (type === 'actual true-up' || type === 'true up')
          return <Link to={`/meterreaddetails/${meterId}`} data-testid="reconcile-link">Reconcile</Link>;

        if (type === 'new' || type === 'new scada') {
          const possibleLocations = availableLocations.filter(
            (loc) => loc && loc !== row.location && loc !== 'Inactive'
          );
          return (
            <Select
              size="small"
              style={{ width: 120 }}
              className={styles.locationSelect}
              popupMatchSelectWidth={false}
              disabled={isApprover}
              value={
                pendingInactiveMeterIds.has(row.meter_computed_id) ? 'Inactive' :
                ignoredMeterIds.has(row.meter_computed_id) || row.remediated === 'ignored' ? 'Ignored' :
                (row.location ?? undefined)
              }
              placeholder="Location"
              onChange={(val) => {
                if (val === '__add_new__') {
                  navigate(`/core-info/${row.meter_id}`);
                } else if (val === 'Inactive') {
                  // Track pending inactive via dedicated state — do NOT write 'Inactive'
                  // into the location field; the meter's actual location name is preserved.
                  setIgnoredMeterIds((prev) => { const n = new Set(prev); n.delete(row.meter_computed_id); return n; });
                  setPendingInactiveMeterIds((prev) => new Set(prev).add(row.meter_computed_id));
                } else if (val === 'Ignored') {
                  setPendingInactiveMeterIds((prev) => { const n = new Set(prev); n.delete(row.meter_computed_id); return n; });
                  setIgnoredMeterIds((prev) => new Set(prev).add(row.meter_computed_id));
                } else {
                  setPendingInactiveMeterIds((prev) => { const n = new Set(prev); n.delete(row.meter_computed_id); return n; });
                  setIgnoredMeterIds((prev) => { const n = new Set(prev); n.delete(row.meter_computed_id); return n; });
                  handleEdit(row.meter_computed_id, 'location', val);
                }
              }}
              options={[
                ...(!pendingInactiveMeterIds.has(row.meter_computed_id) && row.location !== '__ignore__' && row.location !== 'Inactive'
                  ? [{ label: row.location ?? 'Select', value: row.location ?? '' }]
                  : []
                ),
                ...possibleLocations.map((loc) => ({
                  label: String(loc),
                  value: String(loc),
                })),
                { label: '➕ Add', value: '__add_new__' },
                { label: 'Inactive', value: 'Inactive' },
                { label: 'Ignore this meter', value: 'Ignored' },
              ].map((o) => ({ ...o, 'data-testid': 'location-option', 'data-value': toTestAttrValue(o.value) }))}
              data-testid="location-select"
            />
          );
        }

        return '-';
      },
    },
    {
      title: 'Comments',
      key: 'comments',
      width: 200,
      render: (_: unknown, row: MeterRow) => {
        const type = row.meter_read_type?.toLowerCase();
        const needsComment = type === 'negative' || type === 'zero' || type === 'estimate';
        if (!needsComment) return null;
        const isSaved = savedRowIds.has(row.meter_computed_id);
        const isApproved = row.approval_status === 'Approved';
        const showError = attemptedSave.has(row.meter_computed_id) && !row.comments?.trim();
        const isInactiveForThisRowC = Boolean(row.isInactiveThisMonth);
        return (
          <div>
            <Input
              size="small"
              maxLength={200}
              value={row.comments ?? ''}
              disabled={isSaved || isApproved || isApprover || isInactiveForThisRowC}
              status={showError ? 'error' : ''}
              onChange={(e) => handleEdit(row.meter_computed_id, 'comments', e.target.value.replace(/[^\x20-\x7E -￿\uD800-\uDFFF]/g, ''))}
              data-testid="comment-input"
            />
            {showError && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 2 }} data-testid="comment-error">
                Comment is required
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Save',
      key: 'save',
      width: 80,
      render: (_: unknown, row: MeterRow) => {
        const isSaved = savedRowIds.has(row.meter_computed_id);
        const isApproved = row.approval_status === 'Approved'

        const isInactiveForThisRow = Boolean(row.isInactiveThisMonth);
        if (isInactiveForThisRow) {
          return (
            <Tooltip title="Meter is Inactive">
              <button disabled data-testid="undo-row-button" data-tooltip="Meter is Inactive" style={{ padding: '4px 16px', borderRadius: '20px', border: '1px solid #ccc', background: 'transparent', color: '#999', fontSize: '14px', cursor: 'not-allowed', fontFamily: 'inherit' }}>
                Undo
              </button>
            </Tooltip>
          );
        }
        if (isApproved) {
          return (
            <Tooltip title="Cannot undo — meter has been approved">
              <button disabled data-testid="undo-row-button" data-tooltip="Cannot undo — meter has been approved" style={{ padding: '4px 16px', borderRadius: '20px', border: '1px solid #ccc', background: 'transparent', color: '#999', fontSize: '14px', cursor: 'not-allowed', fontFamily: 'inherit' }}>
                Undo
              </button>
            </Tooltip>
          );
        }

        if (isSaved) {
          if (isUndoDisabled(row)) {
            const undoTooltip = isInactiveForThisRow
              ? 'Meter is inactive.'
              : row.approval_status === 'Approved'
              ? 'Cannot undo — meter has been approved'
              : 'Cannot undo — new meter swap is irreversible';
            return (
              <Tooltip title={undoTooltip}>
                <button disabled data-testid="undo-row-button" data-tooltip={undoTooltip} style={{ padding: '4px 16px', borderRadius: '20px', border: '1px solid #ccc', background: 'transparent', color: '#999', fontSize: '14px', cursor: 'not-allowed', fontFamily: 'inherit' }}>
                  Undo
                </button>
              </Tooltip>
            );
          }
          return (
            <button
              onClick={() => handleUndoRow(row.meter_computed_id)}
              data-testid="undo-row-button"
              style={{
                padding: '4px 16px',
                borderRadius: '20px',
                border: '1px solid #004add',
                background: 'transparent',
                color: '#004add',
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#4a6fa5';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#b0b8c8';
              }}
            >
              Undo
            </button>
          );
        }
        return (
          <NSPButton
            onClick={() => {
              const type = row.meter_read_type?.toLowerCase();
              const needsComment = type === 'negative' || type === 'zero' || type === 'estimate';
              if (needsComment && !row.comments?.trim()) {
                setAttemptedSave((prev) => new Set(prev).add(row.meter_computed_id));
                return;
              }
              if (type === 'new' || type === 'new scada') {
                const isIgnored = ignoredMeterIds.has(row.meter_computed_id) || row.remediated === 'ignored';
                if (!isIgnored && !row.location && !pendingInactiveMeterIds.has(row.meter_computed_id)) {
                  setShowLocationWarning(true);
                  return;
                }
                if (isIgnored) {
                  setPendingIgnoreId(row.meter_computed_id);
                } else if (pendingInactiveMeterIds.has(row.meter_computed_id)) {
                  setPendingDecommissionId(Number(row.meter_id));
                  setPendingDecommissionComputedId(row.meter_computed_id);
                } else {
                  setPendingSaveId(row.meter_computed_id);
                }
                return;
              }
              handleSaveRow(row.meter_computed_id);
            }}
            loading={saving}
            disabled={!isSaveEnabled(row) || isApprover}
            data-testid="save-row-button"
          >
            Save
          </NSPButton>
        );
      },
    },
  ];

  // data-column values — sortable headers render through sortIcon(), so name them here
  const cellColumns = withTestColumnAttrs(columns, 'meter-value', {
    location: 'Name',
    meter_number: 'Meter Id',
    units_delivered: 'Gen kWh',
    units_consumed: 'Used kWh',
    prod_stat_netgen: 'Net Gen kWh',
    percent_change_net_units: 'Change (MoM)',
    meter_read_type: 'Meter Read Type',
  });

  return (
    <>
      <PageLayout>
        <div className={styles.pageContent}>
          {saveFeedback && (
            <Alert
              message={saveFeedback.status === 'success' ? 'Save Successful!' : 'Save Failed!'}
              description={saveFeedback.message}
              type={saveFeedback.status}
              showIcon
              closable
              style={{ marginBottom: 16 }}
              data-testid="save-feedback-alert"
              data-type={saveFeedback.status}
            />
          )}
          {moveFeedback && (
            <Alert
              message={
                moveFeedback.status === 'success' ? 'Move Successful!' :
                  moveFeedback.status === 'error' ? 'Move Failed!' : 'Info'
              }
              description={moveFeedback.message}
              type={moveFeedback.status}
              showIcon
              closable
              style={{ marginBottom: 16 }}
              data-testid="move-feedback-alert"
              data-type={moveFeedback.status}
            />
          )}
          <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
              <h1 className={styles.tableTitle} data-testid="page-title">Prod Stat Prep</h1>
              <NSPDatePicker
                picker="month"
                format="MMM YYYY"
                allowClear={false}
                value={pickerValue}
                onChange={handleDateChange}
                showNavigation={false}
                showCalendarIcon={false}
                testId="period-picker"
              />
            </div>
            <ConfigProvider pagination={{ showSizeChanger: true }}>
              <Table<MeterRow>
                dataSource={data}
                columns={cellColumns}
                loading={loading}
                rowKey="meter_computed_id"
                data-testid="prod-stat-prep-table"
                data-loading={String(loading)}
                data-row-count={String(data.length)}
                onRow={(row) => ({
                  'data-testid': 'meter-row',
                  'data-meter-id': String(row.meter_id),
                  'data-meter-number': row.meter_number ?? '',
                  'data-meter-name': row.location ?? '',
                  'data-meter-read-type': toTestAttrValue(row.meter_read_type),
                  'data-remediated': toTestAttrValue(row.remediated),
                  'data-saved': String(savedRowIds.has(row.meter_computed_id)),
                  'data-inactive': String(Boolean(row.isInactiveThisMonth)),
                  'data-approved': String(row.approval_status === 'Approved'),
                }) as TestAttrs}
                pagination={{
                  pageSize,
                  showTotal: (total) => `${total} rows`,
                  onShowSizeChange: (_, size) => setPageSize(size),
                  pageSizeOptions: PAGE_SIZE_OPTIONS,
                  showSizeChanger: { 'data-testid': 'page-size-select' } as SelectProps,
                  itemRender: testPaginationItemRender,
                  'data-testid': 'table-pagination',
                } as TablePaginationConfig}
                scroll={{ x: 'max-content' }}
                tableLayout="fixed"
                rowClassName={(row) => {
                  if (savedRowIds.has(row.meter_computed_id)) return styles.savedRow;
                  if (row.isInactiveThisMonth) return styles.savedRow;
                  return '';
                }}
              />
            </ConfigProvider>
          </div>
        </div>
      </PageLayout>

      <Modal
        open={showLocationWarning}
        width={420}
        title="Action Required"
        okText="OK"
        okButtonProps={{ 'data-testid': 'modal-ok-button' } as ButtonProps}
        cancelButtonProps={{ style: { display: 'none' } }}
        wrapProps={{ 'data-testid': 'location-required-modal' }}
        className="soft-modal"
        onOk={() => setShowLocationWarning(false)}
        onCancel={() => setShowLocationWarning(false)}
      >
        <p>You must select a location, add core info, or ignore the meter before you can save.</p>
      </Modal>

      <Modal
        open={pendingSaveId !== null}
        width={420}
        title="Warning: Irreversible Action"
        okText="Confirm Save"
        cancelText="Cancel"
        className="soft-modal"
        okButtonProps={{ 'data-testid': 'modal-ok-button' } as ButtonProps}
        cancelButtonProps={{ 'data-testid': 'modal-cancel-button' } as ButtonProps}
        wrapProps={{ 'data-testid': 'new-meter-save-modal' }}
        onOk={() => {
          if (pendingSaveId !== null) handleSaveRow(pendingSaveId);
          setPendingSaveId(null);
        }}
        onCancel={() => setPendingSaveId(null)}
      >
        <p>
          You are about to assign a location to a <strong>new</strong> meter. This action{' '}
          <strong>cannot be reversed</strong> once saved.
        </p>
        <p>Are you sure you want to proceed?</p>
      </Modal>

      <Modal
        open={pendingIgnoreId !== null}
        width={460}
        title="Ignore This Meter"
        okText="Continue"
        cancelText="Cancel"
        className="soft-modal"
        okButtonProps={{ 'data-testid': 'modal-ok-button' } as ButtonProps}
        cancelButtonProps={{ 'data-testid': 'modal-cancel-button' } as ButtonProps}
        wrapProps={{ 'data-testid': 'ignore-meter-modal' }}
        onOk={() => {
          if (pendingIgnoreId !== null) handleSaveRow(pendingIgnoreId, { ignored: true });
          setPendingIgnoreId(null);
        }}
        onCancel={() => setPendingIgnoreId(null)}
      >
        <p>
          This meter will not be included in any invoice processing for this month, nor will it
          display on any screens.
        </p>
      </Modal>

      <Modal
        open={pendingDecommissionId !== null}
        width={420}
        title="Mark Meter Inactive"
        okText="Confirm"
        okButtonProps={{ danger: true, 'data-testid': 'modal-ok-button' } as ButtonProps}
        cancelText="Cancel"
        className="soft-modal"
        cancelButtonProps={{ 'data-testid': 'modal-cancel-button' } as ButtonProps}
        wrapProps={{ 'data-testid': 'mark-inactive-modal' }}
        onOk={async () => {
          if (pendingDecommissionId !== null) await handleDecommission(pendingDecommissionId);
          if (pendingDecommissionComputedId !== null) await handleSaveRow(pendingDecommissionComputedId, { markInactive: true });
          if (pendingDecommissionComputedId !== null) {
            setPendingInactiveMeterIds((prev) => { const n = new Set(prev); n.delete(pendingDecommissionComputedId); return n; });
          }
          setPendingDecommissionId(null);
          setPendingDecommissionComputedId(null);
        }}
        onCancel={() => { setPendingDecommissionId(null); setPendingDecommissionComputedId(null); }}
      >
        <p>
          You are about to mark this meter as <strong>Inactive</strong>. This action is{' '}
          <strong>irreversible</strong> and cannot be undone.
        </p>
        <p>
          The meter will be excluded from all future invoicing cycles. Historical data will remain
          accessible for previous months.
        </p>
        <p>Are you sure you want to proceed?</p>
      </Modal>

      <EstimationBlade
        open={bladeOpen}
        onClose={() => setSelectedMeter(null)}
        meter={selectedMeter}
        monthYear={bladeMonthYear}
        meterData={meterData}
        energySource={energySource}
        regionalData={regionalData}
        saving={bladeSaving}
        applyError={applyError}
        onApply={handleApply}
        onMonthChange={setBladeMonthYear}
        readOnly={isApprover || (selectedMeter ? isRowOriginallyApproved(selectedMeter.meter_computed_id) : false)}
      />
    </>
  );
};

export default ProdStatValidateList;